import { Download } from 'lucide-react';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import Button from '@/Components/Marketing/Button';
import Logo from '@/Components/Marketing/Logo';
import Section from '@/Components/Marketing/Section';
import { Body, Heading } from '@/Components/Marketing/Typography';

/**
 * The guides' covers are A4 pages (530 by 750 in the CMS's "medium"
 * format); the frame holds that shape before the image arrives.
 */
export const GUIDE_COVER = Object.freeze({ width: 530, height: 750 });

/** The width a cover is drawn at: its frame's height (224px, 256px from sm) less padding, at A4. */
const GUIDE_COVER_SIZES = '(min-width: 640px) 181px, 158px';

/**
 * The guides to download (the CMS `resources`): each its cover, title,
 * description and a button to the file itself (a new tab: the CMS serves
 * it). Nothing to show while they load; the section steps aside when
 * there are none, or when the request fails.
 */
export default function Guides({ status, guides }) {
    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });
    const headingId = useId();

    if (status !== 'ready' || guides.length === 0) return null;

    return (
        <Section tone="light" aria-labelledby={headingId} className="border-t border-mk-line">
            <Heading id={headingId}>{t('site.resources.guides.title')}</Heading>
            <Body className="mt-5">{t('site.resources.guides.text')}</Body>

            <ul role="list" className="mt-14 grid grid-cols-1 gap-6 sm:mt-16 md:grid-cols-2 lg:grid-cols-3">
                {guides.map((guide) => (
                    <li key={guide.id ?? guide.slug}>
                        <Guide guide={guide} downloadLabel={t('site.resources.guides.download')} />
                    </li>
                ))}
            </ul>
        </Section>
    );
}

function Guide({ guide, downloadLabel }) {
    const titleId = useId();
    const url = guide.content?.url;

    return (
        <article data-guide={guide.slug} aria-labelledby={titleId} className="flex h-full flex-col overflow-hidden rounded-mk-card bg-mk-surface ring-1 ring-mk-line">
            <div className="flex h-72 items-center justify-center bg-mk-sunken px-6 py-8 sm:h-80">
                {guide.featured_image ? (
                    <img
                        src={guide.featured_image}
                        srcSet={guide.featured_image_srcset ?? undefined}
                        sizes={guide.featured_image_srcset ? GUIDE_COVER_SIZES : undefined}
                        alt=""
                        width={GUIDE_COVER.width}
                        height={GUIDE_COVER.height}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-auto rounded-sm bg-mk-surface object-contain shadow-mk-soft ring-1 ring-mk-ink/5"
                    />
                ) : (
                    <GeneratedCover title={guide.title} />
                )}
            </div>
            <div className="flex flex-1 flex-col p-6">
                <h3 id={titleId} className="text-lg font-semibold leading-snug tracking-[-0.015em] text-balance text-mk-ink">{guide.title}</h3>
                {guide.description && (
                    <p className="mt-3 line-clamp-4 text-[15px] leading-relaxed text-pretty text-mk-body">{guide.description}</p>
                )}
                {url && (
                    <div className="mt-auto pt-6">
                        <Button href={url} external variant="secondary" icon={Download} aria-describedby={titleId}>
                            {downloadLabel}
                        </Button>
                    </div>
                )}
            </div>
        </article>
    );
}

/**
 * A guide without a cover image gets a plain one: an A4 page in ink with
 * the wordmark at the top and the title under an orange rule at the foot.
 */
export function GeneratedCover({ title }) {
    return (
        <div
            data-generated-cover=""
            className="flex aspect-[53/75] h-full flex-col justify-between rounded-sm bg-mk-ink p-5 text-left shadow-mk-soft"
        >
            <Logo tone="dark" className="h-4" />
            <div>
                <span aria-hidden="true" className="block h-px w-10 bg-mk-night-orange" />
                <p aria-hidden="true" className="mt-3 text-base font-semibold leading-tight text-balance text-mk-night-text">{title}</p>
            </div>
        </div>
    );
}
